import { memo, useCallback, useMemo } from 'react';

import { type AirActionTrackingLocation, useTrackCopiedShareLink } from '@air/analytics';
import { type ShortUrlObjectType } from '@air/api/types';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useShortIdContext } from '@air/provider-short-id';

import { CopyField, type CopyFieldProps } from '~/components/UI/CopyField';
import { NEW_TAB } from '~/constants/urls';

export type SharePublicItemModalProps = {
  type: ShortUrlObjectType.clip | ShortUrlObjectType.board | ShortUrlObjectType.collection;
  id: string;
  url: string;
  trackLocation: AirActionTrackingLocation;
} & AirModalProps;

export const SharePublicItemModal = memo(
  ({ trackLocation, id, type, url, onClose, ...rest }: SharePublicItemModalProps) => {
    const { trackCopiedShareLink } = useTrackCopiedShareLink();
    const { shortId } = useShortIdContext();

    const onCopy = useCallback(() => {
      trackCopiedShareLink({
        id,
        shareId: shortId,
        type,
        location: trackLocation,
      });
    }, [id, shortId, type, trackLocation, trackCopiedShareLink]);

    const menuActions = useMemo<CopyFieldProps['menuActions']>(
      () => [
        {
          text: 'Preview link',
          action: () => {
            window.open(url, NEW_TAB);
          },
        },
      ],
      [url],
    );

    return (
      <Modal isOpen onDismiss={onClose} {...rest}>
        <div className="mb-6 flex items-center justify-between gap-4">
          <ModalTitle>Share a link</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </div>
        <CopyField url={url} onCopy={onCopy} menuActions={menuActions} />
      </Modal>
    );
  },
);

SharePublicItemModal.displayName = 'SharePublicItemModal';
